import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { LandingHero } from '../../components/LandingHero';
import { TrustLocalBand } from '../../components/TrustLocalBand';
import { FaqList } from '../../components/FaqList';
import { serviceOptionValue } from '../../components/HeroLeadForm';
import { findCategory } from '../../data/serviceCategories';
import { business } from '../../data/site';
import { usePageMeta } from '../../hooks/usePageMeta';
const symptoms = ['Squeaking or squealing when you apply the brakes', 'Grinding noise while braking', 'Vibration or pulsation through the pedal or steering wheel', 'A brake pedal that feels soft, low or spongy', 'A brake warning light or ABS light on the dash', 'Longer stopping distance, or the vehicle pulls to one side while braking'];
const faqs = [{
  question: 'How does the free brake inspection work?',
  answer: 'The brake inspection is free with the purchase of brakes. Call the shop and mention it so it can be applied to your visit.'
}, {
  question: 'Will I know what is needed before any work is done?',
  answer: 'Yes. We inspect the brake system, explain what we found, and go over the recommended correction before work is approved.'
}, {
  question: 'Do brake repairs carry a warranty?',
  answer: 'Repairs we perform carry a 1 year / 12,000 mile parts and labor warranty.'
}];
export function BrakeSpecial() {
  const category = findCategory('brake-services');
  usePageMeta('Brake Repair in Oak Ridge, TN | East End Automotive Repair', 'Brake repair and brake service in Oak Ridge, TN. Free brake inspection with the purchase of brakes. Call East End Automotive Repair at (865) 206-2279.');
  return <>
      <LandingHero eyebrow="Oak Ridge, Tennessee" title="Brake Repair in Oak Ridge, TN" intro="Brake noise, a change in pedal feel or a warning light each point somewhere different in the braking system. We inspect the system, confirm what is causing it, and explain what needs replacing now versus what still has service life." crumbs={[{
      label: 'Specials',
      to: '/specials'
    }, {
      label: 'Brakes'
    }]} offer={{
      label: 'Free brake inspection with the purchase of brakes',
      detail: 'The inspection is included when you purchase brakes at East End. Mention it when you call.'
    }} defaultService={serviceOptionValue('brake-services', 'brake-inspections')} sourcePage="special-brake-inspection" data-id="element-856" data-mp="21abiqcpjaxra" />

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-857" data-mp="1npqutw54lrw5">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-858" data-mp="1rprepwazuval">
          <div className="grid gap-12 md:grid-cols-2 lg:gap-16" data-id="element-859" data-mp="1rs3qfrq5s9f7">
            <div data-id="element-860" data-mp="3i3i58xva4fsn">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-861" data-mp="3i2ykl9e8vdax">
                Signs your brakes should be looked at
              </h2>
              <ul className="mt-5 space-y-2.5" data-id="element-862" data-mp="1ah8c2jvwr7vu">
                {symptoms.map(symptom => <li key={symptom} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-863" data-mp="16godrczlly84">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-864" data-mp="1feqk8823pj2s" />
                    {symptom}
                  </li>)}
              </ul>
              <p className="mt-5 max-w-[560px] text-[14.5px] leading-relaxed text-steel/65" data-id="element-865" data-mp="235du2yvkuirz">
                Any of these can be related to normal wear or to something more
                involved. Because braking is a safety system, it is worth having
                inspected rather than waiting to see if it changes.
              </p>
            </div>

            <div data-id="element-866" data-mp="3tt80olcq8nt">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-867" data-mp="3ucr48mqn33f">
                Brake services at East End
              </h2>
              <ul className="mt-5 grid gap-2.5 sm:grid-cols-2" data-id="element-868" data-mp="a6p2ubhch41i">
                {(category?.services ?? []).map(service => <li key={service.slug} data-id="element-869" data-mp="k6oa85212ocs">
                    <Link to={`/services/brake-services/${service.slug}`} className="group flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-chalk px-3.5 py-3 text-[14px] leading-snug text-steel/85 transition-colors duration-150 ease-out hover:border-ink/40 hover:text-ink" data-id="element-870" data-mp="k5mzy2su65w4">
                      {service.name}
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-steel/40 group-hover:text-brand-dark" aria-hidden="true" data-id="element-871" data-mp="18ltsenjpzql" />
                    </Link>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-charcoal py-12 text-white lg:py-14" data-id="element-872" data-mp="2xb1ow2l90ean">
        <div className="mx-auto flex max-w-shell flex-col gap-6 px-5 sm:flex-row sm:items-center sm:justify-between lg:px-8" data-id="element-873" data-mp="2sf2vg0l2c63d">
          <div data-id="element-874" data-mp="2ig5creabj6sa">
            <h2 className="font-display text-[22px] font-bold tracking-tight text-brand-light sm:text-[26px]" data-id="element-875" data-mp="2ign4pypy6yue">
              Free brake inspection with the purchase of brakes
            </h2>
            <p className="mt-2 max-w-[560px] text-[15px] leading-relaxed text-chalk/70" data-id="element-876" data-mp="29o3enmnq22l6">
              This offer applies when brakes are purchased at East End
              Automotive Repair.
            </p>
          </div>
          <a href={business.phoneHref} className="inline-flex w-full max-w-full shrink-0 items-center justify-center gap-3 self-start whitespace-normal rounded-md bg-brand px-4 py-3.5 text-center font-display text-[17px] font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:self-auto sm:whitespace-nowrap sm:px-6" data-id="element-877" data-mp="1zqf9e2shpcor">
            <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-878" data-mp="1l371bauh9nh0" />
            Call {business.phone}
          </a>
        </div>
      </section>

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-879" data-mp="1758q2ebge75q">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-880" data-mp="1c3a7yvx580fm">
          <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-881" data-mp="1zrteb5wmu352">
            How the inspection and explanation work
          </h2>
          <ol className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-882" data-mp="23v99rywytxg6">
            {[{
            label: 'Condition',
            copy: 'We start with the noise, feel or warning you described.'
          }, {
            label: 'Cause',
            copy: 'We inspect pads, rotors, hydraulics and related components.'
          }, {
            label: 'Correction',
            copy: 'We explain what we found and the options before work begins.'
          }, {
            label: 'Confirm',
            copy: 'We verify the concern is resolved before you leave.'
          }].map((step, i) => <li key={step.label} data-id="element-883" data-mp="31mfpbmzbmx1f" data-mpi={i}>
                <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded bg-ink font-display text-[13px] font-bold text-brand-light" data-id="element-884" data-mp="31o909egyw3f8">
                  {i + 1}
                </span>
                <h3 className="mt-3 font-display text-[16px] font-bold tracking-tight text-ink" data-id="element-885" data-mp="2nw2ixp22vqe7">
                  {step.label}
                </h3>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-steel/70" data-id="element-886" data-mp="2iv52bgwpv4et">
                  {step.copy}
                </p>
              </li>)}
          </ol>
        </div>
      </section>

      <TrustLocalBand data-id="element-887" data-mp="3udpnlxpqcu4l" />

      <FaqList faqs={faqs} heading="Brake service questions" data-id="element-888" data-mp="1smfmkjpugj1f" />

      <section className="w-full bg-ink py-14 text-white lg:py-16" data-id="element-889" data-mp="1soaa85si2p97">
        <div className="mx-auto flex max-w-shell flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-890" data-mp="1wp2k91974y6j">
          <h2 className="max-w-[600px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-[32px]" data-id="element-891" data-mp="21lszxnh39rrf">
            Call East End about brake repair in Oak Ridge, TN
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0" data-id="element-892" data-mp="2bcnk8maryha8">
            <a href={business.phoneHref} className="inline-flex w-full max-w-full items-center justify-center gap-3 whitespace-normal rounded-md bg-brand px-4 py-4 text-center font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:whitespace-nowrap sm:px-7" data-id="element-893" data-mp="2gagv3sn0vzgu">
              <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-894" data-mp="uxvukh50jo4m" />
              Call {business.phone}
            </a>
            <Link to="/specials" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-895" data-mp="nfxx5b03w3hr">
              All Specials
            </Link>
          </div>
        </div>
      </section>
    </>;
}